/* eslint-disable max-len */
import { DEFAULT_STATE, PRICE_RANGE, SUBSCRIPTION_TIER } from '../constants'
import { searchPageStore } from '../index'

/**
 * Gets the current priceRanges value, always returns an array
 * @returns {string[]} - Array of price ranges, defaults to ["free"] if empty/undefined
 */
const getCurrentPriceRanges = () => {
  const state = searchPageStore.getState()
  const { priceRanges } = state

  if (!Array.isArray(priceRanges)) {
    return []
  }

  return priceRanges
}

/**
 * Sets the onSale filter
 * @param {boolean} onSale - Whether to filter for items on sale
 */
export const setOnSale = (onSale) => {
  searchPageStore.setState({ onSale })
}

/**
 * Sets the withSubscription filter
 * @param {boolean} withSubscription - Whether to filter for items with a subscription
 */
export const setWithSubscription = (withSubscription) => {
  if (withSubscription) {
    const currentPriceRanges = getCurrentPriceRanges()
    const filteredRanges = currentPriceRanges.filter((p) => p !== PRICE_RANGE.FREE)

    searchPageStore.setState({ withSubscription, priceRanges: filteredRanges })
    return
  }

  searchPageStore.setState({ withSubscription, subscriptionTier: DEFAULT_STATE.subscriptionTier })
}

/**
 * Sets the subscription tier filter. Any tier, All included, turns the subscription filter on;
 * only Reset turns it off again.
 * @param {string} tier - One of SUBSCRIPTION_TIER
 */
export const setSubscriptionTier = (tier) => {
  const currentPriceRanges = getCurrentPriceRanges()

  searchPageStore.setState({
    withSubscription: true,
    subscriptionTier: tier === SUBSCRIPTION_TIER.ALL ? undefined : tier,
    priceRanges: currentPriceRanges.filter((p) => p !== PRICE_RANGE.FREE),
  })
}

/**
 * Sets the price ranges filter. Quick-select buckets and the custom min/max range override each
 * other, so any non-empty selection resets minPrice/maxPrice.
 * @param {string[]} priceRanges - Array with price range values
 */
export const setPriceRanges = (priceRanges) => {
  if (priceRanges.includes(PRICE_RANGE.FREE)) {
    searchPageStore.setState({
      priceRanges: [PRICE_RANGE.FREE],
      minPrice: DEFAULT_STATE.minPrice,
      maxPrice: DEFAULT_STATE.maxPrice,
      withSubscription: false,
      subscriptionTier: DEFAULT_STATE.subscriptionTier,
    })
    return
  }

  if (priceRanges.length === 0) {
    searchPageStore.setState({ priceRanges })
    return
  }

  searchPageStore.setState({
    priceRanges,
    minPrice: DEFAULT_STATE.minPrice,
    maxPrice: DEFAULT_STATE.maxPrice,
  })
}

/**
 * Adds a price range to the array and resets the custom min/max range
 * @param {string} priceRange - Price range to add
 */
export const addPriceRange = (priceRange) => {
  if (priceRange === PRICE_RANGE.FREE) {
    searchPageStore.setState({
      priceRanges: [PRICE_RANGE.FREE],
      minPrice: DEFAULT_STATE.minPrice,
      maxPrice: DEFAULT_STATE.maxPrice,
      withSubscription: false,
      subscriptionTier: DEFAULT_STATE.subscriptionTier,
    })
    return
  }

  const currentPriceRanges = getCurrentPriceRanges()

  if (currentPriceRanges.length === 1 && currentPriceRanges[0] === PRICE_RANGE.FREE) {
    searchPageStore.setState({
      priceRanges: [priceRange],
      minPrice: DEFAULT_STATE.minPrice,
      maxPrice: DEFAULT_STATE.maxPrice,
    })
    return
  }

  if (!currentPriceRanges.includes(priceRange)) {
    searchPageStore.setState({
      priceRanges: [...currentPriceRanges, priceRange],
      minPrice: DEFAULT_STATE.minPrice,
      maxPrice: DEFAULT_STATE.maxPrice,
    })
  }
}

/**
 * Removes a price range from the array
 * @param {string} priceRange - Price range to remove
 */
export const removePriceRange = (priceRange) => {
  const currentPriceRanges = getCurrentPriceRanges()

  const newPriceRanges = currentPriceRanges.filter((p) => p !== priceRange)
  searchPageStore.setState({ priceRanges: newPriceRanges })
}

/**
 * Sets the minimum price filter. A typed value clears the quick-select buckets; clearing the
 * input leaves them untouched.
 * @param {number|string} minPrice - Minimum price value or empty string
 */
export const setMinPrice = (minPrice) => {
  if (minPrice === DEFAULT_STATE.minPrice) {
    searchPageStore.setState({ minPrice })
    return
  }

  searchPageStore.setState({ minPrice, priceRanges: DEFAULT_STATE.priceRanges })
}

/**
 * Sets the maximum price filter. A typed value clears the quick-select buckets; clearing the
 * input leaves them untouched.
 * @param {number|string} maxPrice - Maximum price value or empty string
 */
export const setMaxPrice = (maxPrice) => {
  if (maxPrice === DEFAULT_STATE.maxPrice) {
    searchPageStore.setState({ maxPrice })
    return
  }

  searchPageStore.setState({ maxPrice, priceRanges: DEFAULT_STATE.priceRanges })
}

/**
 * Clears all pricing filters
 */
export const clearPricing = () => {
  searchPageStore.setState({
    onSale: DEFAULT_STATE.onSale,
    withSubscription: DEFAULT_STATE.withSubscription,
    subscriptionTier: DEFAULT_STATE.subscriptionTier,
    priceRanges: DEFAULT_STATE.priceRanges,
    minPrice: DEFAULT_STATE.minPrice,
    maxPrice: DEFAULT_STATE.maxPrice,
  })
}
